<template>
  <section v-if="!!item && !comparison" class="prompt-content">
    <Settings v-if="showSettings" :item="item" @cancel="$emit('cancelEditingSettings')" @submit="$e => $emit('submit', $e)" />
    <template v-else>
      <div class="left">
        <div class="header">
          <div class="header-buttons">
            <div class="import-export">
              <Button class="button" title="Toggle options" :disabled="isBusy" @click="() => (toggleOptions = !toggleOptions)">
                <MdIcon size="md" :name="toggleOptions ? 'chevron-up' : 'chevron-down'" />
              </Button>
              <div class="title">
                <div v-if="currentView === 'draft'" class="tag draft">DRAFT</div>
                <div v-if="viewActiveVersion" class="tag active">ACTIVE</div>
                <div v-if="currentView === 'preview' && canActivate" class="tag preview">#{{ preview.version }}</div>
                <div class="chain-name">{{ item.name }}</div>
              </div>
              <div>
                <Button
                  class="button"
                  title="Edit chain properties"
                  :disabled="isBusy"
                  @click="() => $router.push({ path: `/chains/${active.id}/edit` })"
                >
                  <MdIcon size="md" name="pencil" />
                </Button>
                <Button class="button" title="Export" :disabled="isBusy" @click="exportPrompt">
                  <md-icon name="export" />
                </Button>
                <Button class="button" title="Import" :disabled="isBusy" @click="importModalVisible = true">
                  <md-icon name="import" />
                </Button>
              </div>
            </div>
            <div class="actions">
              <div v-if="$hasPermission('prompts.write') && hasChanges" class="split-button">
                <div class="main">
                  <Button variant="text" color="primary" :disabled="isUpdateRequestPending" @click="onSave">
                    <md-icon name="content-save" />
                  </Button>

                  <Button
                    v-if="currentView !== 'draft'"
                    variant="text"
                    color="primary"
                    :disabled="isUpdateRequestPending"
                    @click="saveMenuVisible = !saveMenuVisible"
                  >
                    <md-icon :name="saveMenuVisible ? 'chevron-up' : 'chevron-down'" />
                  </Button>
                </div>
                <div v-if="saveMenuVisible" class="menu">
                  <div class="menu-item">
                    <Button variant="text" color="primary" :disabled="isUpdateRequestPending" @click="saveVersion">
                      <md-icon name="content-save" />
                      <span>Save As Version</span>
                    </Button>
                  </div>
                  <div class="menu-item">
                    <Button variant="text" color="secondary" :disabled="isUpdateRequestPending" @click="saveAsDraft">
                      <md-icon name="content-save" />
                      <span>Save As Draft</span>
                    </Button>
                  </div>
                </div>
              </div>
              <Button v-if="!isPromptStreaming" class="button" title="Execute chain" @click="exec">
                <md-icon name="fast-forward" />
              </Button>
              <Button v-else title="Stop execution" class="button" @click="cancel">
                <md-icon name="stop" class="red" />
              </Button>
            </div>
          </div>
          <ul v-if="toggleOptions" class="options">
            <li class="row">
              <div class="content">
                Insert in <Multiselect v-model="options.insertIn" :options="insertInSectionOptions" placeholder="Section" /> at
                <Multiselect v-model="options.insertAt" :options="insertAtOptions" placeholder="Postion" />
              </div>
            </li>
          </ul>
          <div class="add-buttons">
            <Button color="primary" :disabled="isBusy" @click="addPrompt">+ Prompt</Button>
            <Button color="primary" :disabled="isBusy" @click="addQuery">+ Query</Button>
            <Button color="primary" :disabled="isBusy" @click="addTool">+ Tool</Button>
            <Button color="primary" :disabled="isBusy" @click="addTemplate">+ Template</Button>
            <Button color="primary" :disabled="isBusy" @click="addForm">+ Form</Button>
            <Button v-if="contentSources.length" color="primary" :disabled="isBusy" @click="addContent">+ Content</Button>
            <Button v-if="!hasChat" color="secondary" :disabled="isBusy" @click="addChat">+ Chat</Button>
          </div>
        </div>

        <div class="body" :class="{ expanded: toggleOptions }">
          <TransitionGroup name="list" tag="ul">
            <li v-for="(link, index) in chain" :key="link.key">
              <ChainLink
                :item="link"
                :template="localTemplate"
                :chain="chain"
                :index="index"
                :index-in-group="chain.filter(l => l.type === link.type).indexOf(link)"
                :first="index == 0"
                :context="context"
                :last="index == chain.length - 1"
                @change="onChange($event, index)"
                @delete="onDelete(index)"
                @execute="onExecuteOne(index)"
                @up="onMoveUp(index)"
                @down="onMoveDown(index)"
              />
            </li>
          </TransitionGroup>
          <ChatSettings v-if="hasChat" :item="item" :settings="chat" @change="onChatChange($event)" @delete="onDeleteChat()" />
        </div>
      </div>

      <div class="middle"></div>
      <div class="right">
        <div class="content">
          <div class="tabs">
            <Button variant="text" class="tab" :class="{ active: dialogTabActive }" @click="currentTab = 'dialog'">Dialog</Button>
            <Button v-if="!isPromptStreaming" class="tab" variant="text" :class="{ active: contextTabActive }" @click="currentTab = 'context'"
              >Context</Button
            >
            <Button
              v-if="hasInvention && !isPromptStreaming"
              class="tab"
              variant="text"
              :class="{ active: documentsTabActive }"
              @click="currentTab = 'documents'"
              >Documents</Button
            >
            <Button
              v-if="hasChatSkills && !isPromptStreaming"
              class="tab"
              variant="text"
              :class="{ active: skillsTabActive }"
              @click="currentTab = 'skills'"
              >Skills<span v-if="activeSkillsCount" class="skills-badge">{{ activeSkillsCount }}</span></Button
            >
            <Button v-if="!isPromptStreaming" class="tab" variant="text" :class="{ active: historyTabActive }" @click="currentTab = 'history'"
              >History</Button
            >
            <Button
              v-if="currentRun.response.length && !isPromptStreaming"
              class="tab clear-chat-button"
              variant="text"
              title="Clear conversation"
              @click="clearConversation"
            >
              <MdIcon name="delete-outline" size="md" />
            </Button>
          </div>
          <div class="tab-content">
            <div></div>
            <div v-if="dialogTabActive" class="output-container">
              <Output :context="context" @play="exec" />
            </div>
            <div v-if="dialogTabActive && (hasChat || hasLinks)" class="chat-input">
              <div v-if="attachedFiles.length" class="attached-files">
                <span
                  v-for="(file, i) in attachedFiles"
                  :key="file._key || i"
                  class="attached-file"
                  :class="{
                    pending: file.pending,
                    'has-preview': !file.pending && file.mimeType && file.mimeType.startsWith('image/') && file.content,
                    'has-text-preview': !file.pending && file.type === 'text' && file.content
                  }"
                  :title="file.name"
                  :style="
                    !file.pending && file.mimeType && file.mimeType.startsWith('image/') && file.content
                      ? {
                          '--preview-src': `url('${
                            file.content.startsWith('data:') ? file.content : `data:${file.mimeType};base64,${file.content}`
                          }')`
                        }
                      : {}
                  "
                >
                  <span class="attached-file-thumb">
                    <img
                      v-if="!file.pending && file.mimeType && file.mimeType.startsWith('image/') && file.content"
                      :src="file.content.startsWith('data:') ? file.content : `data:${file.mimeType};base64,${file.content}`"
                      class="thumb-img"
                      :alt="file.name"
                    />
                    <MdIcon v-else :name="file.pending ? 'progress-clock' : 'file-outline'" size="sm" :class="{ spin: file.pending }" />
                  </span>
                  <span class="attached-file-name">{{ file.name }}</span>
                  <Button color="secondary" class="remove-attachment" @click="removeAttachment(i)">
                    <MdIcon name="close" size="sm" />
                  </Button>
                  <div v-if="!file.pending && file.type === 'text' && file.content" class="text-preview-popup">
                    <div class="text-preview-content">{{ file.content.slice(0, 800) }}{{ file.content.length > 800 ? '\u2026' : '' }}</div>
                  </div>
                </span>
              </div>
              <div class="chat-input-row">
                <FileSelect
                  accept=".docx,.pptx,.pptm,.pdf,.txt,.eml,.md,.png,.jpg,.jpeg,.gif,.webp,.bmp,.svg"
                  title="Attach file"
                  :disabled="isBusy || attachProcessing"
                  :multiple="true"
                  @change="attachFilesFromDisk"
                >
                  <MdIcon size="md" :name="attachProcessing ? 'progress-clock' : 'paperclip'" />
                </FileSelect>
                <textarea
                  ref="chatTextarea"
                  v-model="chatMessage"
                  :disabled="isBusy"
                  placeholder="Type a message..."
                  @keydown.enter.prevent="sendMessage"
                ></textarea>
                <Button class="send-button" :disabled="isBusy" @click="sendMessage">Send</Button>
              </div>
            </div>
            <Context v-else-if="contextTabActive" v-model="context" />
            <History
              v-else-if="historyTabActive"
              :item="item"
              :loading="isComparing"
              @activate="onActivate"
              @view="onView"
              @compare="onCompare"
              @delete="onDeleteHistory"
            />
            <InventionDocuments
              v-else-if="documentsTabActive"
              :processing-ids="processingDocIds"
              :attached-ids="attachedDocIds"
              @use-doc="onDocumentUse"
            />
            <SkillsPanel
              v-else-if="skillsTabActive"
              :active-skills="
                sessionActiveSkills !== null ? sessionActiveSkills : effectiveChat && effectiveChat.activeSkills ? effectiveChat.activeSkills : []
              "
              :allowed-skills="effectiveChat && effectiveChat.skills ? effectiveChat.skills : []"
              @update:active-skills="onSessionActiveSkillsChanged"
            />
          </div>
        </div>
      </div>
    </template>
    <ImportPromptModal v-if="importModalVisible" @import="doImport" @cancel="importModalVisible = false" />
  </section>
  <section v-else-if="!!comparison" class="comparison">
    <div class="header">
      <h1>{{ comparison.version1 }}</h1>
      <MdIcon name="file-compare" size="lg" />
      <h1>{{ comparison.version2 }}</h1>
      <Button class="button" title="Close" :disabled="isBusy" @click="() => (comparison = null)">
        <MdIcon size="md" name="close" />
      </Button>
    </div>
    <div ref="diffContainer" class="diff" v-html="comparison.html" />
  </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import ImportPromptModal from './ImportContentChainModal.vue';
import Button from '@/components/common/Button';
import MdIcon from '@/components/common/MdIcon';
import Output from './Output.vue';
import ChainLink from './ChainLink';
import ChatSettings from './body/ChatSettings.vue';
import Context from './Context.vue';
import History from './History.vue';
import Settings from './_CreateOrEdit.vue';
import Multiselect from '@/components/common/Multiselect';
import 'jsondiffpatch/lib/formatters/styles/html.css';
import { cloneDeep } from 'lodash';
import { objectsDiffAsHtml } from './diff';
import { isChainIncompatibleWithPioneer } from './pioneerEditable';
import { v4 as uuidv4 } from 'uuid';
import InventionDocuments from './InventionDocuments.vue';
import FileSelect from '@/components/common/FileSelect';
import SkillsPanel from '@/components/skills/SkillsPanel';
import { convertFiles } from '@/utils/filesConverter';
import { downloadInventionDocEntries } from '@/utils/inventionDocDownloader';

function deepCloneArray(arr) {
  if (!Array.isArray(arr)) {
    throw new Error('Input is not an array');
  }

  return arr.map(item => {
    if (Array.isArray(item)) {
      return deepCloneArray(item);
    } else if (typeof item === 'object' && item !== null) {
      return deepCloneObject(item);
    } else {
      return item;
    }
  });
}

function deepCloneObject(obj) {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  const clone = {};

  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (Array.isArray(obj[key])) {
        clone[key] = deepCloneArray(obj[key]);
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        clone[key] = deepCloneObject(obj[key]);
      } else {
        clone[key] = obj[key];
      }
    }
  }

  return clone;
}

export default {
  components: {
    ChainLink,
    Output,
    Button,
    MdIcon,
    ImportPromptModal,
    Context,
    ChatSettings,
    Settings,
    Multiselect,
    History,
    InventionDocuments,
    FileSelect,
    SkillsPanel
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    showSettings: {
      type: Boolean,
      default: false
    }
  },

  data() {
    const localChain = this.item.template?.chain ? JSON.parse(JSON.stringify(this.item.template.chain)) : [];
    localChain.filter(l => l.type === 'form').forEach(form => form.variables.forEach(v => (v.origin = v.origin || 'form')));
    localChain.forEach(link => (link.type = link.type ?? 'prompt'));
    localChain.forEach((link, index) => (link.key = `${link.type}_${link.name}_${index}`));

    const options = this.item.template?.options
      ? JSON.parse(JSON.stringify(this.item.template.options))
      : {
          insertIn: undefined,
          insertAt: undefined
        };
    return {
      localTemplate: {
        ...this.item,
        template: {
          chain: localChain,
          options
        }
      },
      chain: localChain ?? [],
      importModalVisible: false,
      options,
      memo: {
        chain: JSON.parse(JSON.stringify(localChain)),
        chat: this.item.template?.chat ? JSON.parse(JSON.stringify(this.item.template.chat)) : null,
        options: JSON.parse(JSON.stringify(options))
      },
      context: { organizations: this.item.organizations },
      currentTab: 'dialog',
      chatMessage: '',
      chat: this.item.template.chat || null,
      inventionChat: null,
      shouldFocus: false,
      toggleOptions: false,
      comparison: null,
      isComparing: false,
      saveMenuVisible: false,
      editableInPioneer: this.item.editableInPioneer || false,
      itemId: this.$route.params.id || this.$route.query.id,
      attachedFiles: [],
      attachProcessing: false,
      processingDocIds: [],
      sessionActiveSkills: null
    };
  },
  computed: {
    ...mapState({
      preview: s => s.prompts.preview,
      draft: s => s.prompts.draft,
      active: s => s.prompts.item,
      currentView: s => s.prompts.currentView,
      history: s => s.prompts.history,
      isUpdateRequestPending: s => s.prompts.isUpdateRequestPending,
      sessionContext: s => s.prompts.sample.sessionContext,
      isRequestPending: s => s.prompts.sample.isRequestPending,
      isRequestFailed: s => s.prompts.sample.isRequestFailed,
      isPromptStreaming: s => s.prompts.sample.isRequestPending,
      currentRun: s => s.prompts.sample,
      contentSources: s => s.nlp.contentSources ?? [],
      models: s => s.nlp.models || [],
      providers: s => s.nlp.providers || [],
      currentView: s => s.prompts.currentView,
      currentViewKey: s => s.prompts.currentViewKey,
      learnedSkills: s => s.prompts.sample.learnedSkills
    }),
    ...mapGetters({
      isBusy: 'prompts/isBusy'
    }),
    attachedDocIds() {
      return this.attachedFiles.filter(f => !f.pending && f._key).map(f => f._key);
    },
    canActivate() {
      if (this.currentView == 'draft') {
        return true;
      }

      if (this.currentView === 'preview') {
        return !this.viewActiveVersion;
      }

      return false;
    },
    viewActiveVersion() {
      return this.currentView !== 'draft' && (this.currentView === 'item' || this.preview?.version === this.active?.version);
    },
    insertInSectionOptions() {
      return [
        '',
        'Title',
        'PriorityClaim',
        'TechnicalField',
        'Background',
        'Summary',
        'BriefDescription',
        'DetailedDescription',
        'Claims',
        'Abstract',
        'Document'
      ];
    },
    insertAtOptions() {
      return ['', 'Start', 'End', 'Replace'];
    },
    dialogTabActive() {
      return this.currentTab === 'dialog';
    },
    contextTabActive() {
      return this.currentTab === 'context';
    },
    historyTabActive() {
      return this.currentTab === 'history';
    },
    skillsTabActive() {
      return this.currentTab === 'skills';
    },
    requiresInvention() {
      return this.chain.find(l => l.type === 'query' || l.type === 'template');
    },
    requiresContext() {
      return this.chain.find(l => l.type === 'template' || l.type === 'query');
    },
    hasChanges() {
      return (
        JSON.stringify(this.chain ?? {}) !== JSON.stringify(this.memo.chain ?? {}) ||
        JSON.stringify(this.chat ?? {}) !== JSON.stringify(this.memo.chat ?? {}) ||
        JSON.stringify(this.options ?? {}) !== JSON.stringify(this.memo.options ?? {})
      );
    },
    hasChat() {
      return !!this.chat;
    },
    hasLinks() {
      return !!this.chain.length;
    },
    chatChain() {
      return this.hasChat && !this.hasLinks;
    },
    contextForRequest() {
      const copy = { ...this.context };
      delete copy.document;
      delete copy.organizations;

      // Always use the live session invention — `this.context` is only snapshotted on create.
      const invention = this.sessionContext?.invention || copy.invention;
      const organizationId = this.getClientOrganization();
      const organization =
        this.context.organization?.id
          ? this.context.organization
          : organizationId
            ? { id: organizationId }
            : undefined;

      return {
        ...copy,
        ...(invention ? { invention } : {}),
        ...(this.context.document ?? {}),
        ...(organization ? { organization } : {}),
        debug: true
      };
    },
    hasInvention() {
      return !!this.sessionContext.invention;
    },
    documentsTabActive() {
      return this.currentTab === 'documents';
    },
    activeSkillsCount() {
      const active =
        this.sessionActiveSkills !== null
          ? this.sessionActiveSkills
          : this.effectiveChat && this.effectiveChat.activeSkills
          ? this.effectiveChat.activeSkills
          : [];
      return active.length || 0;
    },
    hasChatSkills() {
      return !!(this.effectiveChat && this.effectiveChat.skills && this.effectiveChat.skills.length);
    },
    effectiveChat() {
      return this.chat || this.inventionChat;
    }
  },
  watch: {
    learnedSkills: {
      handler(newVal) {
        if (!newVal) return;
        const allKnownNames = [
          ...new Set([
            ...(this.sessionActiveSkills !== null
              ? this.sessionActiveSkills
              : this.effectiveChat && this.effectiveChat.activeSkills
              ? this.effectiveChat.activeSkills
              : []),
            ...(this.effectiveChat && this.effectiveChat.skills ? this.effectiveChat.skills : [])
          ])
        ];
        const current = this.sessionActiveSkills !== null
          ? [...this.sessionActiveSkills]
          : this.effectiveChat && this.effectiveChat.activeSkills
          ? [...this.effectiveChat.activeSkills]
          : [];
        let changed = false;
        for (const name of newVal) {
          if (!current.includes(name) && allKnownNames.includes(name)) {
            current.push(name);
            changed = true;
          }
        }
        if (changed) {
          this.sessionActiveSkills = current;
        }
      },
      deep: true
    },
    chain: {
      handler(newValue, oldValue) {
        if (this.chain.length === 0) {
          this.chat = null;
        }
      },
      deep: true
    },
    'sessionContext.invention': {
      async handler(newVal, oldVal) {
        this.context = {
          ...this.context,
          invention: newVal || null
        };

        if (newVal) {
          this.$store.dispatch('inventionFiles/loadFiles', newVal.references);
        }

        if (this.chat) {
          return;
        }

        this.inventionChat = null;
        this.inventionChat = (await this.$store.dispatch('prompts/sample/getChat', newVal)).template.chat;
      }
    }
  },
  async created() {
    const chatPomise = this.$store.dispatch('prompts/sample/getChat', this.sessionContext.invention);
    const promises = [
      chatPomise,
      this.$store.dispatch('prompts/getDocumentTypes'),
      this.$store.dispatch('prompts/sample/reset', this.chain),
      this.$store.dispatch('prompts/sample/getDocumentation'),
      this.$store.dispatch('nlp/getConfigurations'),
      this.$store.dispatch('nlp/getSkills'),
      this.$store.dispatch('organizations/getCollection')
    ];
    if (this.sessionContext.invention) {
      promises.push(this.$store.dispatch('inventionFiles/loadFiles', this.sessionContext.invention.references));
    }
    await Promise.all(promises);
    this.inventionChat = (await chatPomise).template.chat;
    this.context = {
      ...this.context,
      ...this.sessionContext
    };
  },
  mounted() {
    document.addEventListener('keydown', this.doSave);
  },
  async beforeDestroy() {
    document.removeEventListener('keydown', this.doSave);
    this.$store.dispatch('prompts/sample/closeStream');
  },
  methods: {
    async onDeleteHistory(event) {
      if (event.type === 'draft') {
        await this.deleteDraft();
      } else {
        const confirm = await this.$confirm({
          title: 'Delete version?',
          message: `Are you sure you want to delete Version ${event.item.version}?`,
          confirm: 'Delete'
        });

        if (!confirm) {
          return;
        }

        await this.$store.dispatch('prompts/deleteHistoryEntry', event.item);
      }
    },
    async deleteDraft() {
      const confirm = await this.$confirm({
        title: 'Discard draft?',
        message: `Are you sure you want to discard this draft?`,
        confirm: 'Discard'
      });

      if (!confirm) {
        return;
      }
      await this.$store.dispatch('prompts/deleteDraft', this.active.id);
    },
    async onCompare(event) {
      this.isComparing = true;
      try {
        let obj1;
        let obj2;
        let version1;
        let version2;
        let version;

        switch (this.currentView) {
          case 'item':
            obj1 = cloneDeep(this.active);
            version1 = `[ACTIVE] Version ${this.active.version} (${new Date(this.active.createdAt).toLocaleString()})`;
            break;
          case 'draft':
            obj1 = cloneDeep(this.draft.data);
            version1 = `Draft`;
            break;
          case 'preview':
            obj1 = cloneDeep(this.preview.data);
            version1 = `Version ${this.preview.version} (${new Date(this.preview.createdAt).toLocaleString()})`;
            break;
        }

        switch (event.type) {
          case 'draft':
            obj2 = cloneDeep(event.item.data);
            version2 = 'Draft';
            break;
          case 'version':
            version = await this.$store.dispatch('prompts/getHistoryEntry', event.item);
            obj2 = version.data;
            version2 = `Version ${version.version} (${new Date(version.createdAt).toLocaleString()})`;
            break;
        }
        this.comparison = {
          version1,
          version2,
          html: await objectsDiffAsHtml(obj1, obj2)
        };
      } finally {
        this.isComparing = false;
      }
    },
    async onView(event) {
      if (this.hasChanges) {
        const confirm = await this.$confirm({
          title: `You have unsaved changes.`,
          message: `Are you sure you want to leave page? All changes will be lost.`,
          confirm: 'Continue'
        });

        if (!confirm) {
          return;
        }
      }

      switch (event.type) {
        case 'draft':
          await this.$store.dispatch('prompts/switchView', 'draft');
          break;
        case 'version':
          await this.$store.dispatch('prompts/viewHistoryEntry', { ...event.item });
          break;
      }
    },
    async onActivate(event) {
      const confirm = await this.$confirm({
        title: `Activate ${event.type}?`,
        message: `Are you sure you want to activate this ${event.type}?`,
        confirm: 'Activate',
        userResponseLabel: event.type === 'draft' ? 'Comment' : ''
      });

      if (!confirm) {
        return;
      }

      try {
        switch (event.type) {
          case 'draft':
            await this.saveDraft();
            await this.$store.dispatch('prompts/activateDraft', { id: this.itemId, comment: confirm.userResponse });
            break;
          case 'version':
            await this.$store.dispatch('prompts/activateHistoryEntry', { ...event.item });
            break;
        }
        if (history.error) {
          this.$toast.error({
            title: `Failed to activate ${event.type}`,
            message: history.error.message
          });
        } else {
          this.$toast.success({
            title: `${event.type === 'draft' ? 'Draft' : `Version ${event.version}`} activated`
          });
        }
      } catch (error) {
        this.$toast.error({
          title: `Failed to activate ${event.type}`,
          message: error.message
        });
      }
    },
    doSave(e) {
      if (!(e.keyCode === 83 && e.ctrlKey)) {
        return;
      }

      e.preventDefault();
      this.onSave();
    },
    async sendMessage() {
      const userMessage = this.chatMessage.trim();
      if (!userMessage) {
        return;
      }

      const chat = this.chat ? this.chat : this.inventionChat;

      if (!chat) {
        return;
      }

      this.shouldFocus = true;
      const systemMessage = chat.messages.filter(message => message.role === 'system');

      const chatForPayload = {
        ...chat,
        messages: [
          ...systemMessage,
          {
            role: 'user',
            content: userMessage
          }
        ]
      };

      if (this.sessionActiveSkills !== null) {
        chatForPayload.activeSkills = this.sessionActiveSkills;
      }

      const chatPayload = {
        ...this.item,
        context: {
          runId: uuidv4(),
          ...this.contextForRequest
        },
        attachedFiles: [...this.attachedFiles].filter((f, i, arr) => !f.id || arr.findIndex(x => x.id === f.id) === i),
        template: {
          options: this.options,
          chat: chatForPayload
        }
      };

      try {
        this.chatMessage = '';
        this.attachedFiles = [];
        await this.$store.dispatch('prompts/sample/sendChatMessage', chatPayload);
      } catch (error) {
        this.$toast.error({
          title: 'Error sending message',
          message: 'There was an error sending your message. Please try again.'
        });
      } finally {
        this.$nextTick(() => {
          if (this.shouldFocus) {
            this.$refs.chatTextarea.focus();
            this.shouldFocus = false;
          }
        });
      }
    },
    refreshMemo() {
      this.memo = {
        chain: this.chain ? JSON.parse(JSON.stringify(this.chain)) : null,
        chat: this.chat ? JSON.parse(JSON.stringify(this.chat)) : null,
        options: this.options ? JSON.parse(JSON.stringify(this.options)) : null
      };
    },
    getClientOrganization() {
      const invention = this.sessionContext?.invention;
      if (!invention) return null;
      const reference = invention.references?.find(r => r.startsWith('HARRITY:'));
      if (!reference) return null;
      const alias = reference
        .split(':')
        .pop()
        .split('-')[0];
      return this.$store.state.organizations.collection?.find(o => o.aliases.includes(alias))?.id ?? null;
    },
    async attachFilesFromDisk(files, pendingKey = null) {
      if (!files || !files.length) return;
      this.attachProcessing = true;
      try {
        const imageFiles = [];
        const otherFiles = [];
        for (const f of Array.from(files)) {
          if ((f.type || '').startsWith('image/')) imageFiles.push(f);
          else otherFiles.push(f);
        }

        // Read images directly as base64
        const imageEntries = await Promise.all(
          imageFiles.map(
            f =>
              new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                  const dataUrl = reader.result;
                  const content = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
                  resolve({ name: f.name, mimeType: f.type, type: 'base64', content, source: 'attachment', metadata: {} });
                };
                reader.onerror = reject;
                reader.readAsDataURL(f);
              })
          )
        );

        if (pendingKey !== null) {
          const idx = this.attachedFiles.findIndex(f => f._key === pendingKey && f.pending);
          if (idx !== -1) this.attachedFiles.splice(idx, 1);
        }

        this.attachedFiles.push(...imageEntries);

        if (otherFiles.length) {
          const result = await convertFiles(otherFiles, { organizationId: this.getClientOrganization() });
          for (const file of result) {
            if (!file.error) {
              if (file.type === 'pages' && Array.isArray(file.content)) {
                this.attachedFiles.push(...file.content);
              } else {
                this.attachedFiles.push(file);
              }
            }
          }
        }
      } catch (e) {
        if (pendingKey !== null) {
          const idx = this.attachedFiles.findIndex(f => f._key === pendingKey && f.pending);
          if (idx !== -1) this.attachedFiles.splice(idx, 1);
        }
        this.$toast.error({ title: 'Failed to attach file', message: e.message });
      } finally {
        this.attachProcessing = false;
      }
    },
    async onDocumentUse(doc) {
      const key = doc.id;
      if (this.attachedFiles.some(f => f._key === key)) return;
      this.attachedFiles.push({ _key: key, name: doc.name, pending: true });
      this.processingDocIds.push(key);
      try {
        const entries = await this._prepareInventionDocEntries(doc);
        const idx = this.attachedFiles.findIndex(f => f._key === key && f.pending);
        if (idx !== -1) this.attachedFiles.splice(idx, 1);
        this.attachedFiles.push(...entries);
      } catch (e) {
        const idx = this.attachedFiles.findIndex(f => f._key === key && f.pending);
        if (idx !== -1) this.attachedFiles.splice(idx, 1);
        this.$toast?.error({ title: 'Failed to attach file', message: e.message });
      } finally {
        const pidx = this.processingDocIds.indexOf(key);
        if (pidx !== -1) this.processingDocIds.splice(pidx, 1);
      }
    },
    async _prepareInventionDocEntries(doc) {
      return downloadInventionDocEntries(doc, { organizationId: this.getClientOrganization() });
    },
    clearConversation() {
      this.$store.dispatch('prompts/sample/reset', this.chain);
    },
    removeAttachment(index) {
      this.attachedFiles.splice(index, 1);
    },
    getOutputLabel(prompt) {
      var index = this.chain.filter(c => c.type === prompt.type).indexOf(prompt) + 1;
      return `${prompt.type.toUpperCase()}_${index}_OUTPUT`;
    },
    getFileLabel(variable) {
      return variable.label || variable.name;
    },
    getLinkModel(candidate) {
      const models = this.models;
      const model = models.find(m => m.id == candidate.model);
      if (model) {
        return model;
      }

      for (const md of models) {
        if (!md.config) {
          continue;
        }
        var config = JSON.parse(md.config);
        if (config.deployment == candidate.model) {
          return md;
        }
      }

      return null;
    },

    async saveAsDraft() {
      this.saveMenuVisible = false;
      if (!this.hasChanges) {
        return;
      }

      if (this.draft) {
        const confirm = await this.$confirm({
          title: 'Overwrite draft?',
          message: `This chain already has an active draft. Saving these changes will overwrite it.
          
          
          Are you sure you want to continue?`,
          confirm: 'Continue'
        });

        if (!confirm) {
          return;
        }
      }

      await this.saveDraft();
    },
    async onSave() {
      if (this.currentView !== 'draft') {
        await this.saveVersion();
      } else {
        await this.saveDraft();
      }
    },
    async saveDraft() {
      this.saveMenuVisible = false;
      if (!this.hasChanges) {
        return;
      }

      const chain = this.chain.map(link => JSON.parse(JSON.stringify(link)));
      const options = JSON.parse(JSON.stringify(this.options));
      chain.forEach(link => delete link['key']);

      try {
        await this.$store.dispatch('prompts/saveDraft', {
          id: this.itemId,
          item: {
            ...this.item,
            template: {
              chain,
              options,
              ...(this.chat && { chat: this.chat })
            }
          }
        });
        this.$toast.success({
          title: 'Draft saved'
        });
      } catch (error) {
        this.$toast.error({
          title: 'Update failed',
          message: error.message
        });
      } finally {
        this.refreshMemo();
      }
    },
    async saveVersion() {
      this.saveMenuVisible = false;
      if (!this.hasChanges) {
        return;
      }

      const confirm = await this.$confirm({
        title: `Save new version?`,
        message: `Are you sure you want to save a new version?`,
        confirm: 'Save',
        userResponseLabel: 'Comment'
      });

      if (!confirm) {
        return;
      }

      const chain = this.chain.map(link => JSON.parse(JSON.stringify(link)));
      const options = JSON.parse(JSON.stringify(this.options));
      chain.forEach(link => delete link['key']);

      try {
        await this.$store.dispatch('prompts/saveVersion', {
          id: this.itemId,
          item: {
            ...this.item,
            editableInPioneer: this.editableInPioneer,
            template: {
              chain,
              options,
              ...(this.chat && { chat: this.chat })
            },
            comment: confirm.userResponse
          }
        });
        this.$toast.success({
          title: 'Update completed',
          message: `Saved`
        });
      } catch (error) {
        this.$toast.error({
          title: 'Update failed',
          message: `Please, try again later or contact our development team.`
        });
      } finally {
        this.refreshMemo();
      }
    },
    async doImport(templateString) {
      this.importModalVisible = false;
      try {
        const template = JSON.parse(templateString);
        if (!template.chain) {
          template.chain = [];
        }
        template.chain.filter(l => !l.type).forEach(l => (l.type = 'prompt'));
        template.chain.forEach((link, index) => (link.key = `${link.type}_${link.name}_${index}`));

        const validationErrors = await this.$store.dispatch('prompts/validateTemplate', template);

        if (validationErrors?.length) {
          for (const error of validationErrors) {
            this.$toast.error({
              title: 'Import failed',
              message: error
            });
          }
        }

        this.chain = template.chain;
        this.chat = template.chat;
        this.$toast.success({
          message: `Template imported succesfully`
        });
      } catch (error) {
        this.$toast.error({
          title: 'Invalid import string'
        });
      }

      this.verifyCanEditInPioneerFlag();
    },
    async cancel() {
      await this.$store.dispatch('prompts/sample/closeStream');
    },
    async exec() {
      this.currentTab = 'dialog';
      try {
        await this.$store.dispatch('prompts/sample/reset', this.chain);
        await this.$store.dispatch('prompts/sample/exec', {
          ...this.item,
          context: {
            runId: uuidv4(),
            ...this.contextForRequest
          },
          debug: true,
          variables: [],
          template: {
            chain: this.chain,
            options: this.options
          }
        });
      } catch (e) {
        this.$toast.error({
          title: 'Failed to generate sample',
          message: `Please, try again later or contact our development team.`
        });
      }
    },
    async exportPrompt() {
      await navigator.clipboard.writeText(
        JSON.stringify({
          model: this.model,
          max_tokens: this.max_tokens,
          temperature: this.temperature,
          chain: this.chain,
          chat: this.chat
        })
      );
      this.$toast.success({
        message: `Template is copied to clipboard`
      });
    },
    async onExecuteOne(index) {
      this.currentTab = 'dialog';

      try {
        const link = this.chain[index];
        if (link.type === 'form') {
          link.continue = false;
        }

        await this.$store.dispatch('prompts/sample/exec', {
          execOne: true,
          index: this.chain.filter(l => l.type !== 'form').indexOf(link),
          ...this.item,
          context: {
            runId: uuidv4(),
            ...this.contextForRequest
          },
          debug: true,
          variables: this.context.variables ?? [],
          template: {
            chain: [link],
            options: this.options
          }
        });
      } catch (e) {
        this.$toast.error({
          title: 'Failed to generate sample',
          message: `Please, try again later or contact our development team.`
        });
      }
    },
    onChange(item, index) {
      this.chain.splice(index, 1, { ...item });
    },
    onChatChange(item) {
      this.chat = { ...item };
    },
    onSessionActiveSkillsChanged(activeNames) {
      this.sessionActiveSkills = activeNames;
    },
    async onDelete(index) {
      const link = this.chain[index];
      if (
        !(await this.$confirm({
          title: 'Delete Link',
          message: `Are you sure you want to delete "${link.name}" ?`,
          confirm: 'Delete'
        }))
      ) {
        return;
      }

      this.chain.splice(index, 1);
      this.verifyCanEditInPioneerFlag();
    },
    async onDeleteChat() {
      this.chat = null;
    },
    onMoveUp(index) {
      if (index > 0) {
        this.chain.splice(index - 1, 0, this.chain.splice(index, 1)[0]);
        this.verifyCanEditInPioneerFlag();
      }
    },
    onMoveDown(index) {
      if (index < this.chain.length - 1) {
        const itemToMove = this.chain.splice(index, 1)[0];
        this.chain.splice(index + 1, 0, itemToMove);
        this.verifyCanEditInPioneerFlag();
      }
    },
    verifyCanEditInPioneerFlag() {
      if (this.editableInPioneer ?? isChainIncompatibleWithPioneer(this.localTemplate)) {
        this.editableInPioneer = false;
        this.$toast.warning({
          title: 'Template is not editable in Pioneer anymore',
          message: 'Only templates with single prompt or template link and single form are editable in Pioneer.'
        });
      } else {
        if (!this.editableInPioneer && this.item?.editableInPioneer) {
          this.$toast.success({
            title: 'Template is editable in Pioneer again',
            message: 'This template can be edited in Pioneer as it now meets the requirements.'
          });
          this.editableInPioneer = this.item?.editableInPioneer;
        }
      }
    },
    addPrompt() {
      this.chain.push({
        id: crypto.randomUUID(),
        type: 'prompt',
        model: '',
        name: `PROMPT ${this.chain.filter(l => l.type === 'prompt').length + 1}`,
        key: `PROMPT_${this.chain.filter(l => l.type === 'prompt').length + 1}`,
        messages: [
          {
            role: 'system',
            content: 'You are a patent attorney who always answers correctly.'
          },
          {
            role: 'user',
            content: 'Who are you?'
          }
        ],
        max_tokens: null,
        temperature: 0.7
      });

      this.verifyCanEditInPioneerFlag();
    },
    addQuery() {
      this.chain.push({
        id: crypto.randomUUID(),
        type: 'query',
        name: `QUERY ${this.chain.filter(l => l.type === 'query').length + 1}`,
        key: `QUERY_${this.chain.filter(l => l.type === 'query').length + 1}`,
        query: `SELECT text_chunk
FROM paragraphs
WHERE refs && ARRAY[{invention.references}] 
ORDER BY embedding <-> 'TEXT TO SEARCH' 
LIMIT 3`
      });
      this.verifyCanEditInPioneerFlag();
    },
    addTool() {
      this.chain.push({
        id: crypto.randomUUID(),
        type: 'tool',
        name: `TOOL ${this.chain.filter(l => l.type === 'tool').length + 1}`,
        key: `TOOL_${this.chain.filter(l => l.type === 'tool').length + 1}`,
        tool: '',
        args: {}
      });
      this.verifyCanEditInPioneerFlag();
    },
    addTemplate() {
      this.chain.push({
        id: crypto.randomUUID(),
        type: 'template',
        name: `TEMPLATE ${this.chain.filter(l => l.type === 'template').length + 1}`,
        key: `TEMPLATE_${this.chain.filter(l => l.type === 'template').length + 1}`,
        template: ''
      });
      this.verifyCanEditInPioneerFlag();
    },
    addForm() {
      this.chain.push({
        id: crypto.randomUUID(),
        type: 'form',
        name: `FORM ${this.chain.filter(l => l.type === 'form').length + 1}`,
        key: `FORM_${this.chain.filter(l => l.type === 'form').length + 1}`,
        variables: []
      });
      this.verifyCanEditInPioneerFlag();
    },
    addContent() {
      this.chain.push({
        id: crypto.randomUUID(),
        type: 'content',
        name: `CONTENT ${this.chain.filter(l => l.type === 'content').length + 1}`,
        key: `CONTENT_${this.chain.filter(l => l.type === 'content').length + 1}`,
        source: 'file',
        value: []
      });
      this.verifyCanEditInPioneerFlag();
    },
    addChat() {
      this.chat = {
        type: 'chat',
        model: '',
        name: `CHAT`,
        key: `CHAT`,
        messages: [
          {
            role: 'system',
            content: 'You are a patent attorney who always answers correctly.'
          }
        ],
        max_tokens: null,
        temperature: 0.7,
        includeHistory: true
      };
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .content-dif {
  padding: 3px;
  max-width: 1024px;

  &.hidden {
    display: none;
  }
}
::v-deep .added {
  background-color: var(--theme-primary-light);
  color: var(--theme-on-primary);
}

::v-deep .deleted {
  background-color: var(--theme-danger-light);
  text-decoration: line-through;
}

::v-deep .jsondiffpatch-property-name {
  color: var(--theme-on-surface);
}

::v-deep .jsondiffpatch-property-name {
  color: var(--theme-on-surface);
}

::v-deep .jsondiffpatch-value {
  color: var(--theme-on-surface);

  pre {
    max-width: 1024px;
    white-space: pre-wrap; /* Wraps the text */
    word-wrap: break-word; /* Break long words if needed */
  }
}

::v-deep .jsondiffpatch-modified {
  .jsondiffpatch-value.jsondiffpatch-left-value {
    pre {
      background-color: var(--theme-danger-light);
      color: var(--theme-on-primary);
    }
  }
  .jsondiffpatch-value.jsondiffpatch-right-value {
    pre {
      background-color: var(--theme-primary-light);
      color: var(--theme-on-primary);
    }
  }
}

::v-deep .jsondiffpatch-right-value {
  &.jsondiffpatch-value {
    color: var(--theme-surface);
  }

  div {
    color: var(--theme-on-surface);
  }

  pre {
    white-space: pre-wrap;
    max-width: 500px;
  }
}

::v-deep .jsondiffpatch-left-value {
  &.jsondiffpatch-value {
    color: var(--theme-surface);
  }

  div {
    color: var(--theme-on-surface);
  }
}

::v-deep .jsondiffpatch-deleted {
  div {
    background-color: var(--theme-danger-light);
  }
  pre {
    background-color: var(--theme-danger-light);
  }
}

::v-deep .jsondiffpatch-added {
  div {
    background-color: var(--theme-primary-light);
    color: var(--theme-on-primary);
  }
  pre {
    background-color: var(--theme-primary-light);
    color: var(--theme-on-primary);
  }
}

.actions {
  display: flex;

  .split-button {
    display: flex;
    position: relative;

    .main {
      display: flex;

      button {
        padding: 0;
      }
    }

    .menu {
      position: absolute;
      top: calc(100%);
      width: 180px;
      right: 0;
      z-index: 1000;
      background-color: var(--theme-surface);
      border: 1px solid var(--theme-on-primary);

      .menu-item {
        display: flex;
        gap: 5px;
        padding: 5px;
        align-items: center;
        user-select: none;

        button {
          gap: 5px;
          align-items: center;
        }
      }
    }
  }
}
.comparison {
  overflow-y: hidden;

  .header {
    display: flex;
    gap: 10px;
    align-items: center;

    h1 {
      font-size: 1.2rem;
    }
  }
  .diff {
    overflow-y: auto;
    height: 95%;
    padding: 5px;
    background: var(--theme-surface) !important;
    color: var(--theme-on-surface) !important;
    background-color: var(--theme-surface);
    word-wrap: break-word;
  }
}

.chat-input {
  display: flex;
  flex-direction: column;
  padding: 10px;
  border-top: 1px solid var(--theme-on-surface);

  .attached-files {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 6px;

    .attached-file {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      background: var(--theme-surface);
      border: 1px solid var(--theme-on-surface);
      border-radius: 4px;
      padding: 2px 4px;
      max-width: 160px;
      position: relative;

      &.pending {
        opacity: 0.7;
      }

      &.has-text-preview:hover .text-preview-popup {
        display: block;
      }

      &.has-preview:hover::after {
        content: '';
        position: absolute;
        bottom: calc(100% + 6px);
        left: 0;
        width: 280px;
        height: 210px;
        border-radius: 6px;
        background-image: var(--preview-src);
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        background-color: var(--theme-surface);
        border: 1px solid var(--theme-on-surface);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
        z-index: 100;
        pointer-events: none;
      }

      .text-preview-popup {
        display: none;
        position: absolute;
        bottom: calc(100% + 6px);
        left: 0;
        width: 320px;
        max-height: 240px;
        background: var(--theme-surface);
        border: 1px solid var(--theme-on-surface);
        border-radius: 6px;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
        z-index: 100;
        pointer-events: none;
        overflow: hidden;

        .text-preview-content {
          padding: 10px 12px;
          font-size: 11px;
          line-height: 1.5;
          white-space: pre-wrap;
          word-break: break-word;
          overflow: hidden;
          max-height: 240px;
          color: var(--theme-on-surface);
        }
      }

      .attached-file-thumb {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        border-radius: 2px;

        .thumb-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      .attached-file-name {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .spin {
        animation: spin 1s linear infinite;
      }

      .remove-attachment {
        padding: 0;
        min-width: unset;
      }
    }
  }

  .chat-input-row {
    display: flex;
    align-items: center;

    textarea {
      flex: 1;
      padding: 10px;
      border: 1px solid var(--theme-on-surface);
      border-radius: 4px;
      margin: 0 10px;
      resize: none;
      font-size: 14px;
    }

    .send-button {
      padding: 10px;
      background-color: var(--theme-primary);
      color: var(--theme-on-primary);
      border: none;
      border-radius: 4px;
      cursor: pointer;
    }
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.settings {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 10px;
}

.list-move,
.list-enter-active,
.list-leave-active {
  transition: all 0.3s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
.list-leave-active {
  position: absolute;
}

.prompt-content {
  h3 {
    text-transform: uppercase;
    overflow: hidden;
    font-weight: 500;
    padding: 5px;
    display: inline-block;
  }

  .button {
    padding: 0;
    cursor: pointer;
    :hover {
      background-color: var(--theme-on-surface);
      color: var(--theme-surface);
      border-radius: 2px;
    }

    .red {
      color: red;
    }

    i {
      width: 25px;
      height: 25px;
      font-size: 21px;
    }
  }

  width: 100%;
  height: 100%;
  margin: 0 0.5rem;
  overflow: scroll;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  grid-gap: 0.5rem;

  .left {
    height: 100%;
    width: 100%;
    overflow-y: hidden;
    display: grid;
    grid-template-rows: auto 1fr;

    .header {
      top: 0;
      width: 100%;

      .header-buttons {
        display: grid;
        grid-template-columns: 1fr max-content;
        align-items: center;
        justify-content: left;
        border-bottom: 1px solid var(--theme-on-surface);

        .title {
          display: flex;
          align-items: center;
          font-size: 1.2rem;
          font-weight: 600;
          gap: 5px;
          color: var(--theme-on-background-dark);

          .chain-name {
            max-width: 400px;
          }

          .tag {
            padding: 3px;
            border-radius: 4px;
            font-size: 0.85rem;
            align-items: center;
            user-select: none;

            &.draft {
              background-color: var(--theme-secondary);
              color: var(--theme-on-secondary);
            }

            &.active {
              background-color: var(--theme-primary);
              color: var(--theme-on-primary);
            }

            &.preview {
              background-color: var(--theme-secondary);
              color: var(--theme-on-secondary);
            }
          }
        }

        .import-export {
          display: grid;
          grid-template-columns: max-content 1fr max-content;
          grid-gap: 5px;
          align-items: center;
        }
      }

      .add-buttons {
        display: grid;
        grid-template-columns: max-content max-content max-content max-content max-content max-content max-content 1fr;
        padding-top: 5px;
        padding-bottom: 5px;
        gap: 5px;
        width: 100%;
        border-bottom: 1px solid var(--theme-on-surface);
      }
    }

    .body {
      width: 100%;
      overflow-y: scroll;
    }

    .options {
      align-items: flex-start;
      width: 100%;
      padding: 10px;

      border-bottom: 1px solid var(--theme-on-surface);

      .row {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 5px;

        .header {
          font-size: 1rem;
          font-weight: 500;
        }

        .content {
          font-size: 0.85rem;
          display: flex;
          justify-content: flex-start;
          align-items: center;
          gap: 15px;

          .multiselect-wrapper {
            width: 200px;
          }
        }
      }
    }
  }

  .middle {
    border-right: 2px solid var(--theme-on-surface);
    opacity: 0.5;
    height: 100%;
  }

  .right {
    height: 100%;
    overflow-y: hidden;

    .content {
      width: 100%;
      height: 100%;
      overflow-y: hidden;
      top: 0px;
      bottom: 5px;
      display: grid;
      grid-template-rows: max-content 1fr;

      .tabs {
        border-bottom: 1px solid white;
        display: flex;
        align-items: center;

        .tab {
          border-radius: 0px;
          border-top-left-radius: 3px;
          border-top-right-radius: 3px;
          padding: 10px;
          &.active {
            background-color: white;
            color: black;
          }

          .skills-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background-color: #22c55e;
            color: white;
            border-radius: 50%;
            width: 18px;
            height: 18px;
            font-size: 11px;
            font-weight: 600;
            margin-left: 5px;
            line-height: 1;
          }
        }

        .clear-chat-button {
          margin-left: auto;
          opacity: 0.6;
          padding: 10px 14px;
          &:hover {
            opacity: 1;
          }
        }
      }

      .tab-content {
        padding-top: 5px;
        overflow-y: hidden;
        display: grid;
        gap: 5px;
        grid-template-rows: auto 1fr auto;
      }

      .output-container {
        overflow-y: auto;
      }
    }
  }

  .prompt-content-wrapper,
  .sample-input-wrapper {
    display: grid;
    grid-template-rows: max-content minmax(0, 1fr);
    grid-gap: 0.5rem;

    .variables-header {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      border-bottom: 1px solid var(--theme-on-surface);
    }
  }

  .prompt-content-title-wrapper,
  .sample-input-title-wrapper {
    display: grid;
    grid-template-columns: minmax(0, 1fr) max-content;
    justify-content: baseline;
    height: 20px;

    > div {
      display: flex;
      justify-content: flex-end;
      align-items: center;
    }
  }
}
</style>
